import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  AlertTriangle, Activity, RefreshCw, TrendingDown, UserX,
  CheckCircle, XCircle, Clock, DollarSign, Store, ShieldAlert
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { hqMonitoringAPI } from '@/services/api';
import { formatDate } from '@/utils/dateFormat';

const StoreHealthCheck = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const fetchData = useCallback(async () => {
    try {
      const response = await hqMonitoringAPI.getStoreHealthCheck();
      setData(response.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching health check data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 2 minutes
    const interval = setInterval(fetchData, 120000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getHealthBadge = (score) => {
    const styles = {
      healthy: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      warning: 'bg-amber-100 text-amber-700 border-amber-200',
      critical: 'bg-red-100 text-red-700 border-red-200'
    };
    return (
      <Badge className={`${styles[score] || styles.healthy} border`}>
        {score.charAt(0).toUpperCase() + score.slice(1)}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-2" />
          <p className="text-gray-500">Loading health check data...</p>
        </div>
      </div>
    );
  }

  const summary = data?.summary || {};
  const storeHealth = data?.store_health || [];
  const recentAbnormal = data?.recent_abnormal_activities || [];
  const managerCloses = data?.recent_manager_closes || [];

  return (
    <div className="p-6 space-y-6" data-testid="store-health-check-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <ShieldAlert className="w-7 h-7 text-red-600" />
            Store Health Check
          </h1>
          <p className="text-gray-500 mt-1">Monitor abnormal activities, cash shortages, and manager overrides</p>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">
            Last updated: {formatDate(lastRefresh.toISOString())}
          </span>
          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white border-0 shadow-lg">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-xs font-medium">Critical Stores</p>
                <p className="text-2xl font-bold">{summary.stores_critical || 0}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white border-0 shadow-lg">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-xs font-medium">Warning Stores</p>
                <p className="text-2xl font-bold">{summary.stores_warning || 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-amber-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0 shadow-lg">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-xs font-medium">Healthy Stores</p>
                <p className="text-2xl font-bold">{summary.stores_healthy || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0 shadow-lg">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-xs font-medium">Today Alerts</p>
                <p className="text-2xl font-bold">{summary.today_abnormal_activities || 0}</p>
              </div>
              <Activity className="w-8 h-8 text-purple-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-rose-500 to-rose-600 text-white border-0 shadow-lg">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-rose-100 text-xs font-medium">Total Shortage</p>
                <p className="text-xl font-bold">{formatCurrency(summary.total_cash_shortage || 0)}</p>
              </div>
              <TrendingDown className="w-8 h-8 text-rose-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0 shadow-lg">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-xs font-medium">Mgr Overrides</p>
                <p className="text-2xl font-bold">{summary.manager_override_closes || 0}</p>
              </div>
              <UserX className="w-8 h-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Store Health Summary */}
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Store className="w-5 h-5 text-blue-600" />
            Store Health Summary (Last 7 Days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Store</TableHead>
                <TableHead>Health</TableHead>
                <TableHead className="text-center">Alerts</TableHead>
                <TableHead className="text-center">High Severity</TableHead>
                <TableHead className="text-center">Mgr Closes</TableHead>
                <TableHead className="text-right">Total Shortage</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {storeHealth.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    No stores found
                  </TableCell>
                </TableRow>
              ) : (
                storeHealth.map((store) => (
                  <TableRow key={store.store_id} className={
                    store.health_score === 'critical' ? 'bg-red-50' :
                    store.health_score === 'warning' ? 'bg-amber-50' : ''
                  }>
                    <TableCell>
                      <div>
                        <p className="font-medium">{store.store_name}</p>
                        <p className="text-xs text-gray-500">{store.store_code}</p>
                      </div>
                    </TableCell>
                    <TableCell>{getHealthBadge(store.health_score)}</TableCell>
                    <TableCell className="text-center">
                      {store.abnormal_count > 0 ? (
                        <span className="font-semibold text-red-600">{store.abnormal_count}</span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {store.high_severity_count > 0 ? (
                        <Badge variant="destructive">{store.high_severity_count}</Badge>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {store.manager_closes_count > 0 ? (
                        <span className="font-semibold text-amber-600">{store.manager_closes_count}</span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium text-red-600">
                      {store.total_shortage > 0 ? formatCurrency(store.total_shortage) : '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Recent Abnormal Activities */}
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Recent Abnormal Activities
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentAbnormal.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 mx-auto text-emerald-300 mb-4" />
              <p className="text-gray-500">No abnormal activities detected</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Time</TableHead>
                  <TableHead>Store</TableHead>
                  <TableHead>Terminal</TableHead>
                  <TableHead>Opened By</TableHead>
                  <TableHead>Closed By</TableHead>
                  <TableHead>Reasons</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead className="text-right">Variance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentAbnormal.slice(0, 15).map((activity, idx) => (
                  <TableRow key={idx} className={activity.severity === 'high' ? 'bg-red-50' : ''}>
                    <TableCell className="text-sm">
                      {activity.created_at ? formatDate(activity.created_at) : '-'}
                    </TableCell>
                    <TableCell className="font-medium">{activity.store_code}</TableCell>
                    <TableCell>{activity.terminal_id}</TableCell>
                    <TableCell>{activity.opened_by_name}</TableCell>
                    <TableCell>
                      {activity.closed_by_name}
                      {activity.is_manager_close && (
                        <Badge variant="outline" className="ml-2 text-xs">Manager</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {activity.reasons?.map((reason, i) => (
                          <Badge key={i} variant="outline" className="mr-1 text-xs">
                            {reason}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={
                        activity.severity === 'high' 
                          ? 'bg-red-100 text-red-700' 
                          : 'bg-amber-100 text-amber-700'
                      }>
                        {activity.severity}
                      </Badge>
                    </TableCell>
                    <TableCell className={`text-right font-medium ${
                      activity.cash_variance < 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {formatCurrency(activity.cash_variance)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Manager Override Closes */}
      {managerCloses.length > 0 && (
        <Card className="shadow-lg border-blue-200">
          <CardHeader className="bg-blue-50">
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <UserX className="w-5 h-5" />
              Recent Manager Override Closes
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Store</TableHead>
                  <TableHead>Terminal</TableHead>
                  <TableHead>Opened By</TableHead>
                  <TableHead>Closed By (Manager)</TableHead>
                  <TableHead>Closing Time</TableHead>
                  <TableHead className="text-right">Variance</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {managerCloses.map((close, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">{close.store_code}</TableCell>
                    <TableCell>{close.terminal_id}</TableCell>
                    <TableCell>{close.opened_by}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{close.closed_by}</p>
                        <p className="text-xs text-gray-500">{close.closed_by_role}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {close.closing_time ? formatDate(close.closing_time) : '-'}
                    </TableCell>
                    <TableCell className={`text-right font-medium ${
                      close.variance < 0 ? 'text-red-600' : close.variance > 0 ? 'text-green-600' : ''
                    }`}>
                      {formatCurrency(close.variance)}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate text-sm text-gray-500">
                      {close.closing_notes || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Legend */}
      <Card className="bg-gray-50 border-gray-200">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-6 text-sm">
            <span className="font-medium text-gray-700">Health Criteria:</span>
            <div className="flex items-center gap-2">
              <Badge className="bg-red-100 text-red-700">Critical</Badge>
              <span>&gt;5 alerts or &gt;₹5,000 shortage</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-amber-100 text-amber-700">Warning</Badge>
              <span>&gt;2 alerts or &gt;₹1,000 shortage</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-100 text-emerald-700">Healthy</Badge>
              <span>No significant issues</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StoreHealthCheck;
